/**
 * resolveContainerIcon — prefix-routing for custom container icons.
 *
 * Icon string format:
 *   "pika:category/icon-name"        → pikaicons SVG (stroke style, legacy default)
 *   "pika:solid:category/icon-name"  → pikaicons SVG with explicit style
 *   "emoji:☕"                       → Unicode emoji rendered as text
 *   undefined                        → default TimelineIcon (caller handles this)
 *
 * Supported styles: stroke, solid, contrast, duo-solid, duo-stroke
 *
 * Pika icons are loaded via dynamic import and cached in resolvedComponentCache.
 * No React.lazy / Suspense — purely state-driven re-render to avoid Suspense
 * bubbling and ensure the DynamicIcon wrapper (not the default type icon) is
 * always the fallback.
 */

import { type FC, type SVGProps, useState, useEffect } from "react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ContainerIconType = "pika" | "emoji"
export type PikaStyle = "stroke" | "solid" | "contrast" | "duo-solid" | "duo-stroke"

export const PIKA_STYLES: PikaStyle[] = ["stroke", "solid", "contrast", "duo-solid", "duo-stroke"]

export interface ParsedIcon {
  type: ContainerIconType
  /** For pika: "category/name". For emoji: the emoji string. */
  value: string
  /** For pika: the icon style. Defaults to "stroke" for legacy strings. */
  style?: PikaStyle
}

// ---------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------

/** Parse an icon string into type + value. Returns null for undefined/empty. */
export function parseIconString(icon: string | undefined): ParsedIcon | null {
  if (!icon || typeof icon !== "string") return null
  if (icon.startsWith("pika:")) {
    const rest = icon.slice(5)
    if (!rest) return null
    // Check for explicit style: "pika:solid:category/name"
    const colonIdx = rest.indexOf(":")
    if (colonIdx > 0) {
      const maybeStyle = rest.slice(0, colonIdx) as PikaStyle
      if (PIKA_STYLES.includes(maybeStyle)) {
        const value = rest.slice(colonIdx + 1)
        if (!value) return null
        return { type: "pika", value, style: maybeStyle }
      }
    }
    // Legacy format: "pika:category/name" → defaults to stroke
    return { type: "pika", value: rest, style: "stroke" }
  }
  if (icon.startsWith("emoji:")) {
    const value = icon.slice(6)
    if (!value) return null
    return { type: "emoji", value }
  }
  return null
}

// ---------------------------------------------------------------------------
// Resolved component cache + import cache
// ---------------------------------------------------------------------------

/** Fully resolved SVG components — renders synchronously */
const resolvedComponentCache = new Map<string, FC<SVGProps<SVGSVGElement>>>()

/** In-flight or resolved dynamic import promises — shared between preload and lazy */
const pikaImportCache = new Map<string, Promise<{ default: FC<SVGProps<SVGSVGElement>> }>>()

/** Listeners notified when a component finishes resolving */
const resolveListeners = new Set<() => void>()

/**
 * Vite glob import — processes ALL pikaicons SVGs at build time into
 * lazy-loadable chunks. In dev mode Vite resolves them on-the-fly;
 * in production builds each SVG becomes a separate JS chunk loaded on demand.
 *
 * Key: relative path like "./pikaicons/stroke/general/settings.svg"
 * Value: () => Promise<{ default: FC<SVGProps<SVGSVGElement>> }>
 */
const pikaGlobs: Record<PikaStyle, Record<string, () => Promise<{ default: FC<SVGProps<SVGSVGElement>> }>>> = {
  stroke: import.meta.glob<{ default: FC<SVGProps<SVGSVGElement>> }>(
    "./pikaicons/stroke/**/*.svg",
    { query: "?react" },
  ),
  solid: import.meta.glob<{ default: FC<SVGProps<SVGSVGElement>> }>(
    "./pikaicons/solid/**/*.svg",
    { query: "?react" },
  ),
  contrast: import.meta.glob<{ default: FC<SVGProps<SVGSVGElement>> }>(
    "./pikaicons/contrast/**/*.svg",
    { query: "?react" },
  ),
  "duo-solid": import.meta.glob<{ default: FC<SVGProps<SVGSVGElement>> }>(
    "./pikaicons/duo-solid/**/*.svg",
    { query: "?react" },
  ),
  "duo-stroke": import.meta.glob<{ default: FC<SVGProps<SVGSVGElement>> }>(
    "./pikaicons/duo-stroke/**/*.svg",
    { query: "?react" },
  ),
}

function importPikaSvg(categoryAndName: string, style: PikaStyle = "stroke") {
  const cacheKey = `${style}:${categoryAndName}`
  const cached = pikaImportCache.get(cacheKey)
  if (cached) return cached

  // Match the glob key format: "./pikaicons/{style}/{category}/{name}.svg"
  const globKey = `./pikaicons/${style}/${categoryAndName}.svg`
  const glob = pikaGlobs[style]
  const loader = glob[globKey]

  if (!loader) {
    // Icon not found in glob — resolve with empty to avoid retries
    const p = Promise.resolve({ default: (() => null) as unknown as FC<SVGProps<SVGSVGElement>> })
    pikaImportCache.set(cacheKey, p)
    return p
  }

  const p = loader().then((mod) => {
    // Store in resolved cache so future renders are synchronous
    resolvedComponentCache.set(cacheKey, mod.default)
    // Notify any mounted components waiting for this icon
    for (const listener of resolveListeners) listener()
    return mod
  })
  pikaImportCache.set(cacheKey, p)
  return p
}

/**
 * Preload pika SVG modules so they're ready before React renders.
 * Call at startup with cached icon strings from localStorage.
 */
export function preloadPikaIcons(iconStrings: string[]) {
  for (const str of iconStrings) {
    const parsed = parseIconString(str)
    if (parsed?.type === "pika") {
      importPikaSvg(parsed.value, parsed.style ?? "stroke")
    }
  }
}

/**
 * Ensure the dynamic import for a pika icon is in flight.
 * Safe to call multiple times — deduped via pikaImportCache.
 */
export function ensurePikaImport(categoryAndName: string, pikaStyle: PikaStyle = "stroke") {
  const cacheKey = `${pikaStyle}:${categoryAndName}`
  if (!pikaImportCache.has(cacheKey)) {
    importPikaSvg(categoryAndName, pikaStyle)
  }
}

// ---------------------------------------------------------------------------
// PikaIcon — renders synchronously when resolved, re-renders when ready.
// No React.lazy / Suspense — avoids Suspense bubble to parent boundaries
// which would flash the default type icon instead of reserving space.
// ---------------------------------------------------------------------------

export function PikaIcon({ categoryAndName, pikaStyle = "stroke", className, style }: {
  categoryAndName: string
  pikaStyle?: PikaStyle
  className?: string
  style?: React.CSSProperties
}) {
  const cacheKey = `${pikaStyle}:${categoryAndName}`
  // Subscribe to resolve events so we re-render when the icon finishes loading
  const [, setTick] = useState(0)

  useEffect(() => {
    // Already resolved — nothing to wait for
    if (resolvedComponentCache.has(cacheKey)) return

    // Ensure import is in flight
    ensurePikaImport(categoryAndName, pikaStyle)

    const listener = () => {
      if (resolvedComponentCache.has(cacheKey)) {
        setTick((t) => t + 1)
        resolveListeners.delete(listener)
      }
    }
    resolveListeners.add(listener)
    return () => { resolveListeners.delete(listener) }
  }, [cacheKey, categoryAndName, pikaStyle])

  // Synchronous render if resolved
  const Resolved = resolvedComponentCache.get(cacheKey)
  if (Resolved) {
    return <Resolved className={className} style={style} />
  }

  // Not yet resolved — render nothing (sized placeholder handled by parent wrapper)
  return null
}

// ---------------------------------------------------------------------------
// ContainerIcon — renders a custom icon or returns null (use default)
// ---------------------------------------------------------------------------

interface ContainerIconProps {
  icon: string | undefined
  className?: string
  /** Size in CSS units (default: "1.25rem" = 20px) */
  size?: string
  /** Color for SVG icons (pika). Emojis ignore this. */
  color?: string
}

/**
 * Render a custom container icon. Returns null when `icon` is undefined
 * or invalid — the caller should fall back to TimelineIcon in that case.
 */
export function ContainerIcon({ icon, className, size = "1.25rem", color }: ContainerIconProps) {
  const parsed = parseIconString(icon)
  if (!parsed) return null

  if (parsed.type === "emoji") {
    return (
      <span
        className={className}
        style={{ fontSize: size, lineHeight: 1 }}
        role="img"
        aria-label={parsed.value}
      >
        {parsed.value}
      </span>
    )
  }

  // pika
  return <PikaIcon categoryAndName={parsed.value} pikaStyle={parsed.style ?? "stroke"} className={className} style={{ width: size, height: size, color }} />
}
